// @flow
import React, { Component } from 'react';
import { StyleSheet, View, Image } from 'react-native';
import { Separator, Text } from 'trpc-react-native-ui';
import { isEqual } from 'lodash';
import CollapsibleFrame from '../../../components/common/Frame/CollapsibleFrame';
import config from '../../../config';
import I18n from '../../../lib/i18n';
import theme from '../../../theme-config';
import { DateTimeLabel } from '../../../components/common';
import DeliveryMethod from '../../../components/signUp/DeliveryMethod';
import DeliveryAddress from '../../../components/signUp/DeliveryAddress';
import { DeliveryStatus } from './DeliveryStatus';
import type { DeliveryResourceType } from '../../../modules/DeliveryResource/reducer';
import DeliveryTimeline from './DeliveryTimeline';

class PendingDeliveryPanel extends Component<Props, State> {
  styles = getStyles();

  state = {
    containerWidth: null,
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  dismiss = () => {
    this.props.dismissDeliveryResource(this.props.deliveryResource.ouid);
  };

  renderTimeline = () => (
    <View>
      <Separator noMargin />
      <Text style={[this.styles.collapsibleFrameTitleText, this.styles.deliveryStatusTitle]}>
        {I18n.t('Dashboard.Orders.deliveryStatus')}
      </Text>
      <DeliveryTimeline status={this.props.deliveryResource.status} />
    </View>
  );

  renderLabel = () => {
    if (this.props.deliveryResource.status === 'delivered') {
      return null;
    }
    return (
      <DeliveryStatus
        trackingId={this.props.deliveryResource.trackingId}
        trackingLink={this.props.deliveryResource.trackingLink}
        status={this.props.deliveryResource.status}
      />
    );
  };

  renderDeliveryStatus = () => {
    if (!this.props.deliveryResource.status) {
      return null;
    }

    switch (config.displayDeliveryStatusInPendingDeliveryPanel) {
      case 'none':
        return null;
      case 'timeline':
        return this.renderTimeline();
      case 'label':
        return this.renderLabel();
      default:
        return null;
    }
  };

  getCollapsibleContent = () => {
    const styles = this.styles;
    const isDeliveryMethodDisplayed = this.props.displayDeliveryMethod && this.props.deliveryResource.deliveryMethod;
    const deliveryAddress = {
      fullName: this.props.deliveryResource.deliveryName,
      ...this.props.deliveryResource.deliveryAddress,
    };

    return (
      <View style={styles.collapsibleFrameContainer} testID={'pendingDeliveryPanel'}>
        <Separator noMargin />
        <View style={styles.collapsibleContentHeader}>
          <Text style={styles.deliveryId}>
            {I18n.t('Dashboard.Orders.deliveryId', { deliveryId: this.props.deliveryResource.deliveryId })}
          </Text>
        </View>
        {isDeliveryMethodDisplayed && (
          <DeliveryMethod
            deliveryMethod={this.props.deliveryResource.deliveryMethod}
            neutralColors
            style={[styles.deliveryItemContainer, this.props.deliveryResource.trackingLink && { marginTop: 0 }]}
          />
        )}
        {this.renderDeliveryStatus()}
        {this.props.deliveryResource.deliveryAddress && (
          <DeliveryAddress deliveryAddress={deliveryAddress} style={styles.deliveryItemContainer} />
        )}
      </View>
    );
  };

  measure = ({ nativeEvent }: any) => this.setState({ containerWidth: nativeEvent.layout.width });

  render() {
    const styles = this.styles;
    return (
      <CollapsibleFrame
        getCollapsibleContent={this.getCollapsibleContent}
        style={[styles.frameContainer, this.props.style]}
        onDismiss={this.dismiss}
        dismissModalContent={I18n.t('Dashboard.Orders.dismissModalContent')}
      >
        <Text style={styles.orderDate}>
          {I18n.t('ordered') + ' '}
          <DateTimeLabel style={styles.orderDate} timestamp={this.props.deliveryResource.orderDate} />
        </Text>
        <View style={styles.container} onLayout={this.measure}>
          <Image
            style={[
              styles.image,
              this.state.containerWidth != null && { width: Math.min(100, this.state.containerWidth / 3) },
            ]}
            source={
              this.props.deliveryResource.status !== 'delivered'
                ? theme.simCardOnItsWayImage
                : theme.inactiveSimCardImage
            }
          />
          {this.state.containerWidth != null && (
            <View
              style={[
                styles.secondColumn,
                this.state.containerWidth != null && { width: (2 * this.state.containerWidth) / 3 },
              ]}
            >
              <Text style={styles.text}>
                {this.props.deliveryResource.status === 'delivered'
                  ? I18n.t('Dashboard.Orders.simDeliveredTitle')
                  : this.props.deliveryResource.status === 'shipped'
                  ? I18n.t('Dashboard.Orders.pendingDeliveryTitle')
                  : I18n.t('Dashboard.Orders.gotYourOrdersTitle')}
              </Text>
              <Text style={[styles.text, styles.description]}>{I18n.t('Dashboard.Orders.activateSIMDescription')}</Text>
            </View>
          )}
        </View>
      </CollapsibleFrame>
    );
  }
}

type Props = DispatchProps & FlaggedProps & OwnProps;

type OwnProps = {
  deliveryResource: DeliveryResourceType,
  style: any,
};

export type DispatchProps = {
  dismissDeliveryResource: Function,
};

export type FlaggedProps = {
  displayDeliveryMethod: boolean,
};

type State = {
  containerWidth: ?number,
};

const getStyles = () =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    frameContainer: {
      paddingTop: 8,
      paddingHorizontal: 8,
      paddingBottom: 8,
    },
    orderDate: {
      textAlign: 'right',
      marginBottom: 10,
      color: theme.gray.normalLight,
    },
    image: {
      height: 100,
      width: 100,
      resizeMode: 'contain',
    },
    text: {
      fontSize: 16,
      textAlign: 'center',
    },
    description: {
      marginTop: 13,
      marginBottom: 18,
      fontWeight: '300',
      fontSize: 14,
    },
    secondColumn: {
      paddingHorizontal: 8,
    },
    collapsibleFrameContainer: {
      marginTop: 14,
    },
    collapsibleFrameTitleText: {
      fontSize: 14,
      fontWeight: 'bold',
    },
    deliveryStatusTitle: {
      marginTop: 10,
    },
    deliveryItemContainer: {
      marginVertical: 15,
    },
    collapsibleContentHeader: {
      marginTop: 8,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    deliveryId: {
      color: theme.frame.textColor,
      fontWeight: '300',
      fontSize: 13,
    },
  });

export default PendingDeliveryPanel;
